import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODU3NTY5NjV8MA&ixlib=rb-4.1.0&q=85";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_magic-link-journey/artifacts/zmxbnelr_Reelty_Logo_R-Only.png";

export default function LoginPage() {
  useAuth(); // keep the hook wired for future guarded redirects
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/auth/request-magic-link", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("Check your inbox — your magic link is on the way.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send login email. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5EBD9]" data-testid="login-page">
      <div className="grid md:grid-cols-2 min-h-screen">
        {/* Left panel — brand + form */}
        <div className="relative flex items-center justify-center p-8 md:p-16 bg-[#1A0F0A] text-[#F5EBD9] overflow-hidden">
          {/* Ambient background — soft radial oxblood glow */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(1200px 700px at 20% 100%, rgba(107,15,26,0.35), transparent 60%), radial-gradient(900px 600px at 100% 0%, rgba(176, 141, 87,0.12), transparent 60%)",
            }}
          />
          <div className="relative z-10 w-full max-w-md">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* Logo stacks ABOVE the wordmark so the visible R-glyph left-edge
                  aligns with the "REELTY / ONBOARDING PORTAL" text edge. The
                  source PNG carries ~31.5% transparent padding (measured from
                  the 864×864 asset), which at a 60px render translates to a
                  19px left inset. -ml-[19px] pulls the img box left by that
                  exact amount so what you SEE lines up on every device. */}
              <div className="flex flex-col items-start gap-2 mb-10">
                <img
                  src={LOGO_URL}
                  alt="Reelty"
                  className="w-[60px] h-[60px] object-contain -ml-[19px]"
                />
                <div>
                  <div className="font-mono-tag text-[10px] tracking-[0.35em] text-[#F5EBD9]">REELTY</div>
                  <div className="font-mono-tag text-[10px] tracking-[0.25em] text-[#78584B]">ONBOARDING PORTAL</div>
                </div>
              </div>
            </motion.div>

            {!sent ? (
              <>
                <motion.h1
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.05 }}
                  className="font-display text-3xl md:text-[42px] font-extrabold tracking-tight leading-[1.05]"
                >
                  {/* Stacked two-line H1 — "Photographer" above "Onboarding".
                      Explicit <br/> plus block-level nowrap spans keep each
                      word on its own line no matter how the container flexes. */}
                  <span className="whitespace-nowrap">Photographer</span><br />
                  <span className="text-[#F5EBD9] whitespace-nowrap">Onboarding</span>
                </motion.h1>
                <p className="text-[#A89684] mt-5 text-[15px] leading-relaxed max-w-[26rem]">
                  One magic link, no passwords needed. Complete your onboarding to unlock the Property Shoots Hub.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
                  <div>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-[#78584B] absolute left-3.5 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                      <Input
                        id="email"
                        data-testid="login-email-input"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="you@studio.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 bg-white border-[#1A0F0A]/15 focus-visible:border-[#6B0F1A] focus-visible:ring-[#6B0F1A]/25 rounded-sm text-[#1A0F0A] placeholder:text-[#A89684]"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="login-submit-btn"
                    className="w-full h-12 bg-[#6B0F1A] hover:bg-[#8A1424] text-[#F5EBD9] font-bold text-sm tracking-wide rounded-sm btn-primary-glow disabled:opacity-70"
                  >
                    {submitting ? "Sending…" : "Send magic link"}
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.25} />
                  </Button>
                </form>

                <div className="mt-10 pt-6 border-t border-[#F5EBD9]/12">
                  <div className="font-mono-tag text-[9px] text-[#A89684]">
                    New here? Ask Reelty Admin for an invite at support@goreelty.com
                  </div>
                </div>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="magic-link-sent"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#B08D57] mt-0.5" strokeWidth={2} />
                  <div>
                    <h2 className="font-display text-2xl font-bold text-[#F5EBD9]">Check your email</h2>
                    <p className="text-[#A89684] text-sm mt-2 leading-relaxed">
                      We sent a secure sign-in link to <span className="text-[#B08D57]">{email}</span>.
                      The link expires in 15 minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setSent(false); setEmail(""); }}
                      data-testid="magic-link-back-btn"
                      className="mt-6 font-mono-tag text-[10px] tracking-[0.25em] text-[#B08D57] hover:text-[#F5EBD9] transition-colors"
                    >
                      ← USE A DIFFERENT EMAIL
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Right panel — hero image */}
        <div className="hidden md:block relative bg-[#F5EBD9] overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(245,235,217,0.05) 0%, rgba(26,15,10,0.35) 100%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
